import { Component, OnInit, Output, Input, EventEmitter, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SelectItem } from 'primeng';
import { Subject } from 'rxjs';
import { debounceTime, first } from 'rxjs/operators';
import { IPendingExpenseFilter } from '../../interfaces/inbox-job-filter';
import { IPageResult } from '../../interfaces/page-result';
import {
  AdditionalChargeService,
  FilterStateService,
  IncidentsService,
  UserService,
} from '../../services';
import { JobStatus } from '../../enums/job-status';
import { AdditionalChargeStatusEnum } from 'src/app/enums/additional-charge.enum';
import { IPendingExpenseInboxItem } from '../../interfaces/pending-expense-inbox-result';
import moment from 'moment/moment';

enum AdditionalChargeStatus {
  Pending = 1,
  Completed = 2,
  Declined = 3,
  SupplierRequest = 4,
  InternalRequest = 5,
  SupplierUpdated = 6,
  InternalUpdate = 7,
}

@Component({
  selector: 'vme-jobs-waiting-approval',
  templateUrl: './vme-jobs-waiting-approval.component.html',
  styleUrls: ['./vme-jobs-waiting-approval.component.scss', './jobs-inbox.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class VmeJobsWaitingApprovalComponent implements OnInit {
  public readonly itemsPerPageDef = 20;
  public readonly defaultSortField = 'id';
  public readonly defaultSortOrder = 1;
  public jobCompletedOrCancelledDate: any = null;
  public lastUpdatedFilterValue: any = null;

  public jobStatuses: SelectItem[] = [
    { label: 'Completed', value: 1 },
    { label: 'Closed', value: 3 },
    { label: 'Cancelled', value: 4 },
    { label: 'Invoiced', value: 2 },
  ];

  public customerInvoicePeriodOptions: SelectItem[] = [
    { label: 'Weekly', value: 7 },
    { label: 'Monthly', value: 0 },
  ];

  public showCustomerInvoicePeriodColumn: boolean = false;

  public additionalChargeTypes: SelectItem[] = [];

  public customerList: SelectItem[] = [];

  public expenseStatuses: SelectItem[] = [];

  public assigneeOptions: SelectItem[] = [];

  public selectedJob: IPendingExpenseInboxItem;
  public page: IPageResult<IPendingExpenseInboxItem> = {
    items: [],
    total: 0,
  };

  public filter: Subject<IPendingExpenseFilter>;
  public filterVal: IPendingExpenseFilter;

  @Output() totalCountChanged: EventEmitter<number> = new EventEmitter<number>();

  //---exported params
  @Output() jobSelectionChanged: EventEmitter<any>;
  @Output() onGridAction: EventEmitter<any>;

  @Input() inboxType: number;

  //---/exported params

  //
  public isLoaded: boolean = false;
  public isLoading: boolean = false;
  public showProgress: boolean = false;
  private currentUserId: string;
  public displayChangeAssigneeDialog: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private additionalChargeService: AdditionalChargeService,
    private filterStateService: FilterStateService,
    private incidentsService: IncidentsService,
    private userService: UserService,
  ) {
    this.jobSelectionChanged = new EventEmitter<any>();
    this.onGridAction = new EventEmitter<any>();
  }

  async ngOnInit() {
    this.isLoading = true;
    this.currentUserId = this.userService.currentUser.id;

    this.initDefaultFilter();
    this.loadQueryParamFilters();

    await this.bindAdditionalChargeTypesWithoutDefault();
    await this.getAssignees();
    await this.getAdditionalChargeStatuses();

    void this.load(this.filterVal, false);
  }

  loadQueryParamFilters() {
    this.route.queryParams.pipe(first()).subscribe((params) => {
      const jobStatusList = JSON.parse(params['jobStatusList'] || '[]');
      if (jobStatusList.length > 0) {
        this.filterVal.filters.jobStatusList.value = jobStatusList;
      }

      const additionalChargeTypeList = JSON.parse(params['additionalChargeTypeList'] || '[]');
      if (additionalChargeTypeList.length > 0) {
        this.filterVal.filters.additionalChargeTypeList.value = additionalChargeTypeList;
      }

      const expenseStatuses = JSON.parse(params['expenseStatuses'] || '[]');
      if (expenseStatuses.length > 0) {
        this.filterVal.filters.expenseStatuses.value = expenseStatuses;
      }

      const assigneeOptions = JSON.parse(params['assigneeOptions'] || '[]');
      if (assigneeOptions.length > 0) {
        this.filterVal.filters.assigneeOptions.value = assigneeOptions;
      }

      const customerList = JSON.parse(params['customerList'] || '[]');
      if (customerList.length > 0) {
        this.filterVal.filters.customerList.value = customerList;
      }

      if (params['completedOrCancelledDateFrom']) {
        this.filterVal.filters.completedOrCancelledDateFrom.value = moment(
          params['completedOrCancelledDateFrom'],
        ).toDate();
      }

      if (params['completedOrCancelledDateTo']) {
        this.filterVal.filters.completedOrCancelledDateTo.value = moment(
          params['completedOrCancelledDateTo'],
        ).toDate();
      }

      this.setJobCompletedOrCancelledDate();

      if (params['lastUpdatedDateFrom']) {
        this.filterVal.filters.lastUpdatedDateFrom.value = moment(
          params['lastUpdatedDateFrom'],
        ).toDate();
      }

      if (params['lastUpdatedDateTo']) {
        this.filterVal.filters.lastUpdatedDateTo.value = moment(
          params['lastUpdatedDateTo'],
        ).toDate();
      }

      this.setLastUpdatedFilterValue();

      if (params['jobNumber']) {
        this.filterVal.filters.jobNumber.value = params['jobNumber'];
      }

      if (params['supplier']) {
        this.filterVal.filters.supplier.value = params['supplier'];
      }

      const customerInvoicePeriods = JSON.parse(params['customerInvoicePeriods'] || '[]');
      if (customerInvoicePeriods.length > 0) {
        this.filterVal.filters.customerInvoicePeriods.value = customerInvoicePeriods;
      }
    });
  }

  setQueryParamFilters() {
    const queryParams: any = {};

    queryParams['selectedTab'] = 5;

    const jobStatusList = this.filterVal.filters.jobStatusList?.value || [];
    if (jobStatusList.length > 0) {
      queryParams['jobStatusList'] = JSON.stringify(jobStatusList);
    }

    const additionalChargeTypeList = this.filterVal.filters.additionalChargeTypeList?.value || [];
    if (additionalChargeTypeList.length > 0) {
      queryParams['additionalChargeTypeList'] = JSON.stringify(additionalChargeTypeList);
    }

    const expenseStatuses = this.filterVal.filters.expenseStatuses?.value || [];
    if (expenseStatuses.length > 0) {
      queryParams['expenseStatuses'] = JSON.stringify(expenseStatuses);
    }

    const assigneeOptions = this.filterVal.filters.assigneeOptions?.value || [];
    if (assigneeOptions.length > 0) {
      queryParams['assigneeOptions'] = JSON.stringify(assigneeOptions);
    }

    const customerList = this.filterVal.filters.customerList?.value || [];
    if (customerList.length > 0) {
      queryParams['customerList'] = JSON.stringify(customerList);
    }

    const customerInvoicePeriods = this.filterVal.filters.customerInvoicePeriods?.value || [];
    if (customerInvoicePeriods.length > 0) {
      queryParams['customerInvoicePeriods'] = JSON.stringify(customerInvoicePeriods);
    }

    if (this.filterVal.filters.completedOrCancelledDateFrom?.value) {
      queryParams['completedOrCancelledDateFrom'] = moment(
        this.filterVal.filters.completedOrCancelledDateFrom.value,
      ).format('YYYY-MM-DD');
    }

    if (this.filterVal.filters.completedOrCancelledDateTo?.value) {
      queryParams['completedOrCancelledDateTo'] = moment(
        this.filterVal.filters.completedOrCancelledDateTo.value,
      ).format('YYYY-MM-DD');
    }

    if (this.filterVal.filters.lastUpdatedDateFrom?.value) {
      queryParams['lastUpdatedDateFrom'] = moment(
        this.filterVal.filters.lastUpdatedDateFrom.value,
      ).format('YYYY-MM-DD');
    }

    if (this.filterVal.filters.lastUpdatedDateTo?.value) {
      queryParams['lastUpdatedDateTo'] = moment(
        this.filterVal.filters.lastUpdatedDateTo.value,
      ).format('YYYY-MM-DD');
    }

    if (this.filterVal.filters.jobNumber?.value) {
      queryParams['jobNumber'] = this.filterVal.filters.jobNumber.value;
    }

    if (this.filterVal.filters.supplier?.value) {
      queryParams['supplier'] = this.filterVal.filters.supplier.value;
    }

    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      queryParamsHandling: 'merge',
    });
  }

  private async getAssignees() {
    await this.incidentsService.getAssignees().then((result) => {
      this.assigneeOptions = result
        .sort((a, b) => a.label.trim().localeCompare(b.label.trim()))
        .map((x) => <SelectItem>{ value: x.value.id, label: x.label });
      this.assigneeOptions.sort((a, b) => {
        if (a.value === this.currentUserId) return -1;
        if (b.value === this.currentUserId) return 1;
        return 0;
      });
      this.assigneeOptions.unshift({ label: 'Unassigned', value: '0' } as SelectItem);
    });
  }

  async getAdditionalChargeStatuses() {
    const expenseStatuses = await this.additionalChargeService.getAdditionalChargeStatuses();
    if (expenseStatuses.length > 0) {
      this.expenseStatuses = expenseStatuses
        .filter(
          (x) =>
            x.id !== AdditionalChargeStatusEnum.Approved &&
            x.id !== AdditionalChargeStatusEnum.Declined,
        )
        .map((x) => {
          return {
            label: x.name,
            value: x.id,
          } as SelectItem;
        });
    }
  }

  load(filter: IPendingExpenseFilter, setQueryParams = true) {
    this.isLoading = true;

    if (setQueryParams) {
      this.setQueryParamFilters();
    }

    this.additionalChargeService.getPendingExpensesInbox(filter).then((result) => {
      const total = result.items.length;
      this.page.items = result.items;
      this.page.total = total;
      this.isLoaded = true;
      this.isLoading = false;

      this.showCustomerInvoicePeriodColumn = result.showCustomerInvoicePeriodColumn;

      this.totalCountChanged.emit(total);

      this.customerList = result.customerOptions;
    });
  }

  private async bindAdditionalChargeTypesWithoutDefault(): Promise<void> {
    const result = await this.additionalChargeService.getAdditionalChargeTypes();

    this.additionalChargeTypes = result.map((x) => ({
      label: x.chargeType,
      value: x.id,
    }));
  }

  initDefaultFilter() {
    this.filterVal = {
      first: 0,
      rows: this.itemsPerPageDef,
      sortField: this.defaultSortField, // ASC
      sortOrder: this.defaultSortOrder,
      filters: {
        jobStatusList: { value: [JobStatus.Cancelled, JobStatus.Completed, JobStatus.Closed] },
        additionalChargeTypeList: { value: null },
        expenseStatuses: {
          value: [
            AdditionalChargeStatus.Pending,
            AdditionalChargeStatus.SupplierUpdated,
            AdditionalChargeStatus.InternalUpdate,
          ],
        },
        assigneeOptions: { value: ['0', this.currentUserId] },
        completedOrCancelledDateFrom: { value: null },
        completedOrCancelledDateTo: { value: null },
        lastUpdatedDateFrom: { value: null },
        lastUpdatedDateTo: { value: null },
        jobNumber: { value: null },
        customerList: { value: null },
        supplier: { value: null },
        customerInvoicePeriods: { value: [0, 7] },
      },
    };
  }

  onRowSelect(event) {
    this.jobSelectionChanged.emit([event.data, this.inboxType]);
  }

  deselectJobFromInbox() {
    this.selectedJob = null;
  }

  deselectJob() {
    this.selectedJob = null;
    this.jobSelectionChanged.emit([null, this.inboxType]);
  }

  markJobAsClicked(jobId) {
    var item = this.page.items.find((o) => o.id == jobId);
    if (item != null && !item.clicked) {
      item.clicked = true;
    }
  }

  gridUpdate() {
    this.load(this.filterVal);
  }

  search() {
    this.filterVal.first = 0; //fix error - when you set page > 1 and filter, then data not display
    this.load(this.filterVal, true);

    this.deselectJob();
  }

  editJob(item, $event) {
    this.filterStateService.inboxJobFilterVal = this.filterVal;
    if ($event?.ctrlKey || $event?.metaKey) {
      // ctrl was held during click
      window.open(`/app/jobscreen/${item.id}`, '_blank');
    } else {
      void this.router.navigate(['app/jobscreen/' + item.id]);
    }
  }

  itemsCountPostfix(value: string, count: number) {
    if (value == null || value == '' || count < 2) return value;
    return value + ' (+' + (count - 1) + ')';
  }

  customersCountPostfix(item: IPendingExpenseInboxItem) {
    return this.itemsCountPostfix(item.customerName, item.customerCount);
  }

  refresh(table) {
    table.sortField = '';
    this.jobCompletedOrCancelledDate = null;
    this.lastUpdatedFilterValue = null;
    this.initDefaultFilter();
    void this.router.navigate([], {
      relativeTo: this.route,
    });
    this.load(this.filterVal, false);
  }

  downloadToCSV() {
    this.additionalChargeService.downloadPendingExpensesInboxCSV(this.filterVal);
  }

  showPriceDetails() {
    const param: any = {
      jobId: this.selectedJob.id,
      customerId: this.selectedJob.customerId,
    };

    if (this.selectedJob.supplierId) {
      param.supplierId = this.selectedJob.supplierId;
    }

    this.router.navigate(['app/pricedetails'], { queryParams: param });
  }

  setCompletedOrCancelledDateFromAndTo(): void {
    const [startDate, endDate] = this.jobCompletedOrCancelledDate;

    console.log('jobCompletedOrCancelledDate', this.jobCompletedOrCancelledDate);

    // Was requested that selecting only one date should every up TO the selected date
    if (!endDate) {
      this.filterVal.filters.completedOrCancelledDateFrom = { value: null };
      this.filterVal.filters.completedOrCancelledDateTo = { value: startDate };
      return;
    }

    this.filterVal.filters.completedOrCancelledDateFrom = { value: startDate };
    this.filterVal.filters.completedOrCancelledDateTo = { value: endDate };
  }

  inboxJob(itemAny: any): IPendingExpenseInboxItem {
    return itemAny;
  }

  private setJobCompletedOrCancelledDate() {
    if (!this.filterVal.filters.completedOrCancelledDateTo?.value) {
      this.jobCompletedOrCancelledDate = null;
      return;
    }

    if (!this.filterVal.filters.completedOrCancelledDateFrom?.value) {
      this.jobCompletedOrCancelledDate = [
        this.filterVal.filters.completedOrCancelledDateTo?.value,
        null,
      ];

      return;
    }

    this.jobCompletedOrCancelledDate = [
      this.filterVal.filters.completedOrCancelledDateFrom?.value,
      null,
    ];
  }

  private setLastUpdatedFilterValue() {
    if (!this.filterVal.filters.lastUpdatedDateTo?.value) {
      this.lastUpdatedFilterValue = null;
      return;
    }

    if (!this.filterVal.filters.lastUpdatedDateFrom?.value) {
      this.lastUpdatedFilterValue = [this.filterVal.filters.lastUpdatedDateTo?.value, null];
      return;
    }

    this.lastUpdatedFilterValue = [
      this.filterVal.filters.lastUpdatedDateFrom?.value,
      this.filterVal.filters.lastUpdatedDateTo?.value,
    ];
  }

  protected assignAllExpenses() {}

  protected setLastUpdatedFromAndTo() {
    const [startDate, endDate] = this.lastUpdatedFilterValue;

    // Was requested that selecting only one date should every up TO the selected date
    if (!endDate) {
      this.filterVal.filters.lastUpdatedDateFrom = { value: null };
      this.filterVal.filters.lastUpdatedDateTo = { value: startDate };
      return;
    }

    this.filterVal.filters.lastUpdatedDateFrom = { value: startDate };
    this.filterVal.filters.lastUpdatedDateTo = { value: endDate };
  }
}
